import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

function unitFromIndex(i: number): number {
    let s = ((i + 1) * 2654435761) >>> 0;
    s ^= s >>> 16;
    s = Math.imul(s, 2246822519) >>> 0;
    s ^= s >>> 13;
    s = Math.imul(s, 3266489917) >>> 0;
    return (s >>> 0) / 0xffffffff;
}

const BAR_COUNT = 15;

const ChartSkeleton: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const barBase = alpha(theme.palette.text.primary, isDark ? 0.10 : 0.08);
    const barHighlight = alpha(theme.palette.text.primary, isDark ? 0.22 : 0.18);
    const barStroke = alpha(theme.palette.text.primary, isDark ? 0.18 : 0.14);
    const lineStroke = alpha(theme.palette.text.primary, isDark ? 0.22 : 0.18);
    const axisStroke = alpha(theme.palette.text.primary, isDark ? 0.13 : 0.10);

    const { bars, linePoints } = useMemo(() => {
        const barHeights = Array.from({ length: BAR_COUNT }, (_, i) => 0.18 + unitFromIndex(i) * 0.78);
        const lineHeights = Array.from({ length: BAR_COUNT }, (_, i) => 0.18 + unitFromIndex(i + 100) * 0.78);
        const vbW = 360;
        const vbH = 160;
        const padX = 14;
        const padY = 12;
        const axisH = 18;
        const plotBottom = vbH - padY - axisH;
        const plotTop = padY + 6;
        const plotH = plotBottom - plotTop;
        const slot = (vbW - padX * 2) / BAR_COUNT;
        const barW = slot * 0.52;

        const barsLocal = barHeights.map((h, i) => {
            const hPx = h * plotH;
            const x = padX + i * slot + (slot - barW) / 2;
            const y = plotBottom - hPx;
            return { x, y, w: barW, h: hPx };
        });

        const linePointsLocal = lineHeights
            .map((h, i) => {
                const cx = padX + i * slot + slot / 2;
                const cy = plotBottom - h * plotH;
                return `${cx},${cy}`;
            })
            .join(' ');

        return { bars: barsLocal, linePoints: linePointsLocal };
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 180,
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <svg
                viewBox="0 0 360 160"
                preserveAspectRatio="none"
                aria-hidden
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    padding: 16,
                    boxSizing: 'border-box',
                }}
            >
                <defs>
                    <linearGradient id="barWave" x1="0%" y1="0%" x2="100%" y2="0%" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor={barBase} />
                        <stop offset="40%" stopColor={barBase} />
                        <stop offset="50%" stopColor={barHighlight} />
                        <stop offset="60%" stopColor={barBase} />
                        <stop offset="100%" stopColor={barBase} />
                        <animateTransform
                            attributeName="gradientTransform"
                            type="translate"
                            from="-360 0"
                            to="360 0"
                            dur="1.6s"
                            repeatCount="indefinite"
                        />
                    </linearGradient>
                </defs>
                <line
                    x1="14"
                    y1="130"
                    x2="346"
                    y2="130"
                    stroke={axisStroke}
                    strokeWidth="1"
                    strokeLinecap="square"
                />
                {bars.map((b, i) => (
                    <rect
                        key={i}
                        x={b.x}
                        y={b.y}
                        width={b.w}
                        height={b.h}
                        rx="2"
                        fill="url(#barWave)"
                        stroke={barStroke}
                        strokeWidth="1.25"
                    />
                ))}
                <polyline
                    fill="none"
                    stroke={lineStroke}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray="4 5"
                    points={linePoints}
                />
            </svg>
        </Box>
    );
};

export default ChartSkeleton;
